import { forwardRef, memo, Ref, useCallback, useEffect, useRef, useState } from 'react';
import { isUnlikelyShift } from '~/components/Request/multiday/utils';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useToggle } from '~/hooks';
import StartClockIcon from '~/images/start-clock.svg';
import WarningIcon from '~/images/warning-icon.svg';
import { Colors } from '~/themes/colors';
import Button from '../Button';
import { Center } from '../Center';
import { Column } from '../Column';
import { InputSelect, ISelectOption } from '../InputSelect';
import { Row } from '../Row';
import { Text } from '../Text';
import { Field, Title, Value } from './Field';
import { IInputJobProps } from './Types';
import { isValidShifts } from './Utils';

const factoryTimeOptions = (): ISelectOption[] => {
  const opts: ISelectOption[] = [];

  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += 15) {
      let formattedHour;
      if (hour === 0) {
        formattedHour = 12;
      } else if (hour > 12) {
        formattedHour = hour - 12;
      } else {
        formattedHour = hour;
      }

      const ampm = hour < 12 ? 'am' : 'pm';
      const formattedHourPadded = formattedHour.toString().padStart(2, '0');
      const formattedMinute = minute.toString().padStart(2, '0');
      const title = `${formattedHourPadded}:${formattedMinute} ${ampm}`;

      opts.push({ title, value: title });
    }
  }

  return opts;
};

const TimeOptions: ISelectOption[] = factoryTimeOptions();

/**
 * Props for the Hours input component.
 * Extends IInputJobProps but omits single-value props in favor of arrival/departure pairs.
 */
interface IHoursProps extends Omit<IInputJobProps<string>, 'defaultValue' | 'onChange' | 'value'> {
  /** Default arrival time value (e.g., "08:00 am") */
  defaultArrivalValue?: string;
  /** Default departure time value (e.g., "05:00 pm") */
  defaultDepartureValue?: string;
  /** Controlled arrival time value */
  valueArrival?: string;
  /** Controlled departure time value */
  valueDeparture?: string;
  /** Callback fired when arrival or departure time changes */
  onChange?(arrival: string, departure: string): void;
}

/**
 * Internal state type for managing arrival and departure times.
 */
type TInternalValue = {
  /** Arrival time in 12-hour format (e.g., "08:00 am") */
  arrival: string;
  /** Departure time in 12-hour format (e.g., "05:00 pm") */
  departure: string;
};

const DEFAULT_ARRIVAL = '08:00 am';
const DEFAULT_DEPARTURE = '05:00 pm';

const Component = (
  {
    defaultArrivalValue,
    defaultDepartureValue,
    isDisabled,
    isOpen,
    valueArrival: controlledArrivalValue,
    valueDeparture: controlledDepartureValue,
    onChange,
    onToggle,
  }: IHoursProps,
  ref: Ref<HTMLDivElement>,
) => {
  const [internalValue, setInternalValue] = useState<TInternalValue>({
    arrival: defaultArrivalValue ?? DEFAULT_ARRIVAL,
    departure: defaultDepartureValue ?? DEFAULT_DEPARTURE,
  });

  const [isShowErrorPopup, controls] = useToggle();
  const errorTypeRef = useRef<'INCORRECT_TIME_ORDER' | 'UNLIKELY_SHIFTS' | undefined>(undefined);

  const isControlled =
    controlledArrivalValue !== undefined && controlledDepartureValue !== undefined;

  useEffect(() => {
    if (isControlled) {
      setInternalValue({
        arrival: controlledArrivalValue ?? DEFAULT_ARRIVAL,
        departure: controlledDepartureValue ?? DEFAULT_DEPARTURE,
      });
    }
  }, [controlledArrivalValue, controlledDepartureValue, isControlled]);

  const wasControlledRef = useRef(isControlled);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && wasControlledRef.current !== isControlled) {
      console.warn(
        `Warning: Hours component is changing from ${wasControlledRef.current ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}. ` +
          'Elements should not switch from controlled to uncontrolled (or vice versa).',
      );
    }
    wasControlledRef.current = isControlled;
  }, [isControlled]);

  const doUpdateValue = useCallback(
    (newArrival: string, newDeparture: string) => {
      controls.setOff();

      if (!isControlled) {
        setInternalValue({
          arrival: newArrival,
          departure: newDeparture,
        });
      }

      if (!isValidShifts(newArrival, newDeparture)) {
        errorTypeRef.current = 'INCORRECT_TIME_ORDER';
        controls.setOn();
        return;
      }

      if (isUnlikelyShift(newArrival, newDeparture)) {
        errorTypeRef.current = 'UNLIKELY_SHIFTS';
        controls.setOn();
        return;
      }

      onToggle?.();
      onChange?.(newArrival, newDeparture);
    },
    [controls, isControlled, onChange, onToggle],
  );

  const handleSelectValueChange = (key: keyof TInternalValue) => (value: string) =>
    setInternalValue((prev) => ({ ...prev, [key]: value }));

  const handleValueChange = useCallback(() => {
    doUpdateValue(
      internalValue.arrival || DEFAULT_ARRIVAL,
      internalValue.departure || DEFAULT_DEPARTURE,
    );
  }, [doUpdateValue, internalValue.arrival, internalValue.departure]);

  const ValueComponent = useCallback(() => {
    const currentArrivalValue = isControlled ? controlledArrivalValue : defaultArrivalValue;
    const currentDepartureValue = isControlled ? controlledDepartureValue : defaultDepartureValue;
    const hasValue = currentArrivalValue && currentDepartureValue;
    const displayText =
      !isOpen && hasValue
        ? `${currentArrivalValue} - ${currentDepartureValue}`
        : 'Arrival/Departure';

    const color = isOpen || !hasValue ? Colors.neutral_300 : Colors.neutral_600;

    return <Value text={displayText} style={{ color }} />;
  }, [
    controlledArrivalValue,
    controlledDepartureValue,
    defaultArrivalValue,
    defaultDepartureValue,
    isControlled,
    isOpen,
  ]);

  return (
    <>
      <Field
        ComponentProps={{
          Button: {
            'data-cy': `input-job.hours.trigger`,
            'data-testid': `input-job.hours.trigger`,
          },
          Container: { 'data-cy': `input-job.hours`, 'data-testid': `input-job.hours` },
        }}
        TitleComponent={TitleComponent}
        ValueComponent={ValueComponent}
        IconSrc={StartClockIcon}
        isDisabled={isDisabled}
        isOpen={isOpen}
        ref={ref}
        onToggle={onToggle}
      >
        <Center flexDirection="column" gap={32}>
          <Text color={Colors.neutral_500} italic textAlign="center">
            Setting “Arrival” 15-30 minutes before the first patient allows professionals to
            <br />
            become familiar with your office and be ready to work.
          </Text>
          <Row alignItems="center" gap={20} paddingInline={60}>
            <Row alignItems="center" gap={12}>
              <Title text="Arrival:" />
              <InputSelect
                dataTestId={`input-job.hours.arrival`}
                dataCy={`input-job.hours.arrival`}
                value={internalValue.arrival || DEFAULT_ARRIVAL}
                setValue={handleSelectValueChange('arrival')}
                options={TimeOptions}
              />
            </Row>
            <Text>/</Text>
            <Row alignItems="center" gap={12}>
              <Title text="Departure:" />
              <InputSelect
                dataTestId={`input-job.hours.departure`}
                dataCy={`input-job.hours.departure`}
                value={internalValue.departure || DEFAULT_DEPARTURE}
                setValue={handleSelectValueChange('departure')}
                options={TimeOptions}
              />
            </Row>
          </Row>

          <Button
            color="secondary"
            data-testId={`input-job.hours.confirm`}
            dataCy={`input-job.hours.confirm`}
            disabled={isDisabled}
            text="Confirm Hours"
            style={{
              height: 48,
              width: 180,
              backgroundColor: isDisabled ? Colors.secondary_100 : Colors.secondary_500,
            }}
            onClick={handleValueChange}
          />
        </Center>
      </Field>
      {isShowErrorPopup && errorTypeRef.current === 'INCORRECT_TIME_ORDER' && (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => <img src={WarningIcon} alt="Warning" style={{ marginBottom: 21 }} />}
          title="Sorry!"
          description="Arrival Time cannot be after Departure Time"
          rightButtonAction={() => {
            errorTypeRef.current = undefined;
            controls.setOff();
          }}
          rightButtonText="Close"
        />
      )}
      {isShowErrorPopup && errorTypeRef.current === 'UNLIKELY_SHIFTS' && (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => <img src={WarningIcon} alt="Warning" style={{ marginBottom: 21 }} />}
          title="Please Review Shift Time"
          description={
            <>
              <Text color="neutral_500" textAlign="center">
                The hours you&apos;ve entered appear to be outside the usual range. If they&apos;re
                accurate, click confirm. Otherwise, you can go back to make edits.
              </Text>
              <Column marginTop={18} gap={10}>
                <Row justifyContent="space-between" marginHorizontal={65}>
                  <Text color="neutral_500" bold>
                    Arrival Time:
                  </Text>
                  <Text color="neutral_500">{internalValue?.arrival}</Text>
                </Row>
                <Row justifyContent="space-between" marginHorizontal={65}>
                  <Text color="neutral_500" bold>
                    Departure Time:
                  </Text>
                  <Text color="neutral_500">{internalValue?.departure}</Text>
                </Row>
              </Column>
            </>
          }
          leftButtonText="Go Back"
          leftButtonAction={() => {
            errorTypeRef.current = undefined;
            controls.setOff();
          }}
          rightButtonAction={() => {
            onToggle?.();
            onChange?.(internalValue.arrival, internalValue.departure);
            controls.setOff();
          }}
          rightButtonText="Confirm"
        />
      )}
    </>
  );
};

const TitleComponent = () => (
  <Row alignItems="center">
    <Title text="Hours:" />
    <Center
      backgroundColor={Colors.neutral_300}
      borderRadius={100}
      height={16}
      marginLeft={8}
      padding="0 4px"
      width={24}
    >
      <Text bold color={Colors.white} fontSize={12}>
        EDT
      </Text>
    </Center>
  </Row>
);

/**
 * Hours input component for selecting arrival and departure times in job postings.
 *
 * Features:
 * - Time selection in 15-minute intervals from 12:00 am to 11:45 pm
 * - Supports both controlled and uncontrolled modes
 * - Displays times in 12-hour format with am/pm indicators
 * - Shows timezone badge (EDT)
 * - Includes helpful guidance text about arrival time recommendations
 * - Automatic field closure after confirmation with smooth UX transition
 *
 * @example
 * ```tsx
 * // Uncontrolled usage
 * <Hours
 *   defaultArrivalValue="08:00 am"
 *   defaultDepartureValue="05:00 pm"
 *   onChange={(arrival, departure) => console.log(arrival, departure)}
 * />
 *
 * // Controlled usage
 * <Hours
 *   valueArrival={arrivalTime}
 *   valueDeparture={departureTime}
 *   onChange={handleTimeChange}
 *   isOpen={isFieldOpen}
 *   onToggle={handleToggle}
 * />
 * ```
 */
export const Hours = memo(forwardRef(Component)) as <T = HTMLDivElement>(
  props: IHoursProps & { ref?: Ref<T> },
) => ReturnType<typeof Component>;
